/**
 * Helper สำหรับกล้องสแกน QR
 *
 * ปัญหาที่เจอ: บนมือถือที่มีกล้องหลังหลายตัว (ultra-wide / macro / tele)
 * getUserMedia({ facingMode: "environment" }) มักเลือกกล้อง ultra-wide ให้
 * ซึ่งโฟกัสระยะใกล้ (10-20 ซม.) ไม่เข้า → ภาพเบลอ สแกนไม่ติด
 * และ browser ไม่ได้เปิด continuous autofocus ให้อัตโนมัติ
 *
 * บน iPhone Pro (13 Pro ขึ้นไป) เจอบ่อยที่สุด เพราะ Safari มักคืน
 * "Back Dual Wide Camera" / "Back Triple Camera" ซึ่งเริ่มที่เลนส์ 0.5x
 * โฟกัสใกล้ไม่เข้า และ Safari ยังไม่รองรับ focusMode constraint
 * ทางแก้บน iOS คือ "เลือก device ให้ถูกตัว" (Back Camera = เลนส์หลัก 1x)
 * + ขอความละเอียดสูงขึ้น เพื่อให้ถือห่างขึ้นแล้วยังอ่าน QR ได้
 *
 * วิธีแก้: เรียก getUserMedia "ครั้งเดียว" ต่อการเปิดสแกน 1 ครั้ง
 * ถ้าอ่าน label ได้ก่อนเปิดกล้องก็เลือกเลนส์เลย (resolvePreferredCamera)
 * ถ้าอ่านไม่ได้ก็ใช้ facingMode ไปก่อน แล้วค่อยจำเลนส์ที่ดีไว้ใช้ครั้งหน้า
 * (rememberBestCamera) — ไม่สลับกลางคัน
 *
 * ห้ามเปิดกล้องเพื่อสำรวจ device ก่อนแสดงผล และห้ามสลับกล้องเป็นรอบ ๆ
 * เพราะ LINE WebView บน Android และ iOS ทุกตัว ขอ permission ใหม่ทุกครั้งที่เรียก
 * getUserMedia ลูกค้าจะเจอ popup รัว ๆ — เคยทำแล้วพัง อย่าทำซ้ำ
 *
 * ข้อจำกัดบน LIFF: LIFF browser ใช้ WKWebView (iOS) / Android WebView
 * บน iOS มักไม่คืน label ของกล้องเลยแม้ให้ permission แล้ว → เลือกเลนส์ไม่ได้
 * เคสนั้นจะได้ facingMode ตามเดิม และถ้าเบลอให้ผู้ใช้ไปทางปุ่มคลังภาพแทน
 */

/**
 * ความละเอียดที่ขอ — สูงกว่า default ของ lib (720x720) มาก
 * เหตุผล: iPhone Pro โฟกัสระยะต่ำสุด ~15-20 ซม. ผู้ใช้ต้องถือห่าง
 * ถ้าเฟรมความละเอียดต่ำ QR จะเหลือ pixel น้อยเกินจนถอดรหัสไม่ได้
 */
const IDEAL_RESOLUTION = {
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  frameRate: { ideal: 30 },
};

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/** เลนส์ที่ไม่เหมาะกับการสแกน QR เลย (โฟกัสใกล้ไม่ได้ หรือไม่ใช่กล้องภาพปกติ) */
const BAD_LENS = /macro|depth|tele|fisheye|monochrome|infrared|\bir\b/i;
const BACK_LENS = /back|rear|environment|หลัง/i;

const scoreCamera = (device) => {
  const label = (device.label || "").toLowerCase().trim();

  if (!label) return -1;
  if (!BACK_LENS.test(label)) return -1; // กล้องหน้า

  let score = 100;
  if (BAD_LENS.test(label)) score -= 80;

  // ultra-wide ดีหรือแย่ขึ้นกับเครื่อง — บน iPhone Pro มี AF + macro
  // ซึ่งกลับเป็นเลนส์ที่โฟกัสใกล้ได้ดีที่สุด แต่บน Android ส่วนใหญ่ fixed focus
  // เลยแค่ลดอันดับบน iOS ไม่ได้ตัดทิ้ง เผื่อ auto-switch วนมาถึง
  if (/ultra/.test(label)) score -= isIOS() ? 20 : 80;

  if (label === "back camera") score += 50; // iOS: กล้องหลักตัวเดียว
  if (/dual|triple/.test(label)) score += 10; // iOS: virtual camera สลับเลนส์เองได้

  // Android Chrome: "camera2 0, facing back" → index ยิ่งน้อยยิ่งเป็นกล้องหลัก
  const idx = label.match(/camera2\s+(\d+)/);
  if (idx) score += Math.max(0, 20 - Number(idx[1]) * 10);

  return score;
};

/**
 * เลนส์ที่ดีที่สุดเท่าที่เคยเห็นใน page load นี้
 *
 * เก็บในหน่วยความจำเท่านั้น ห้ามลง localStorage — deviceId บน iOS เปลี่ยนทุก session
 * เอาของเก่ามาใช้จะได้ OverconstrainedError แล้วต้องเปิดกล้องใหม่ = ขอ permission เพิ่ม
 */
let preferredDeviceId = null;

/** จัดอันดับกล้องหลัง — คืน [] ถ้าอ่าน label ไม่ได้ (แปลว่าเลือกไม่ได้ อย่าเดา) */
const rankBackCameras = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) return [];

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === "videoinput")
      .map((d, index) => ({ device: d, index, score: scoreCamera(d) }))
      .filter((item) => item.score >= 0)
      .sort((a, b) => b.score - a.score || a.index - b.index);
  } catch {
    return [];
  }
};

/**
 * หา deviceId ที่ควรใช้ "ก่อน" เปิดกล้อง — ไม่แตะ getUserMedia เด็ดขาด
 *
 * enumerateDevices() จะคืน label ก็ต่อเมื่อมีสตรีมทำงานอยู่ หรือมี persistent
 * permission (เช่น Chrome บน Android ที่เคยกดอนุญาตไปแล้ว) กรณีนั้นได้เลนส์ที่ถูก
 * ฟรี ๆ ไม่มีต้นทุน popup ส่วนกรณีอ่าน label ไม่ได้ให้คืน null ไปใช้ facingMode แทน
 *
 * @returns {Promise<string|null>}
 */
export const resolvePreferredCamera = async () => {
  if (preferredDeviceId) return preferredDeviceId;

  const ranked = await rankBackCameras();
  if (!ranked.length) return null;

  preferredDeviceId = ranked[0].device.deviceId || null;
  return preferredDeviceId;
};

/**
 * จำเลนส์ที่ดีที่สุดไว้ใช้ครั้งหน้า — เรียกตอนสตรีม live แล้วเท่านั้น
 * เพราะนั่นคือจังหวะเดียวที่ label มองเห็นได้บน iOS/WebView
 *
 * ตั้งใจ "ไม่" สลับกล้องในรอบนี้ การสลับ = ปิดแล้วเปิดกล้องใหม่ = popup เพิ่มอีก 1 ครั้ง
 * ซึ่งเคยทำแล้วลูกค้าเจอ popup รัว ๆ และภาพแย่ลง — ยอมได้เลนส์ถูกช้าไป 1 รอบดีกว่า
 */
export const rememberBestCamera = async () => {
  const ranked = await rankBackCameras();
  if (!ranked.length) return;

  const best = ranked[0].device.deviceId;
  if (best) preferredDeviceId = best;
};

/** ลืมเลนส์ที่จำไว้ — ใช้ตอนเปิดด้วย deviceId แล้วพัง จะได้ถอยไป facingMode */
export const forgetPreferredCamera = () => {
  preferredDeviceId = null;
};

/**
 * สร้าง constraints ให้ <Scanner /> — ถ้ารู้ deviceId ให้ล็อกกล้องตัวนั้น
 * ไม่งั้น fallback เป็น facingMode: environment ตามเดิม
 */
export const buildScannerConstraints = (deviceId) => ({
  ...(deviceId
    ? { deviceId: { exact: deviceId } }
    : { facingMode: { ideal: "environment" } }),
  ...IDEAL_RESOLUTION,
  // browser จะข้าม advanced constraint ที่อุปกรณ์ไม่รองรับ (ไม่ throw)
  advanced: [
    { focusMode: "continuous" },
    { exposureMode: "continuous" },
    { whiteBalanceMode: "continuous" },
  ],
});

/**
 * สั่งเปิด continuous autofocus หลังสตรีมเริ่มแล้ว
 * (บาง browser ไม่สนใจ advanced constraint ตอน getUserMedia แต่รับตอน applyConstraints)
 */
export const enableContinuousFocus = async (track) => {
  if (!track?.getCapabilities) return false;

  let caps = {};
  try {
    caps = track.getCapabilities() || {};
  } catch {
    return false;
  }

  const advanced = [];
  if (caps.focusMode?.includes("continuous"))
    advanced.push({ focusMode: "continuous" });
  if (caps.exposureMode?.includes("continuous"))
    advanced.push({ exposureMode: "continuous" });
  if (caps.whiteBalanceMode?.includes("continuous"))
    advanced.push({ whiteBalanceMode: "continuous" });

  if (!advanced.length) return false;

  try {
    await track.applyConstraints({ advanced });
    return true;
  } catch {
    return false;
  }
};

/** เครื่องนี้เปิดไฟฉายจากในเบราว์เซอร์ได้ไหม (Safari รองรับตั้งแต่ iOS 17.4) */
export const hasTorch = (track) => {
  try {
    return !!track?.getCapabilities?.().torch;
  } catch {
    return false;
  }
};

export const setTorch = async (track, isOn) => {
  try {
    await track.applyConstraints({ advanced: [{ torch: isOn }] });
    return true;
  } catch {
    return false;
  }
};

/**
 * สั่งโฟกัสใหม่ (tap to focus) — กระตุก focusMode ไป manual แล้วกลับมา continuous
 * เพื่อบังคับให้ AF วิ่งหาโฟกัสรอบใหม่
 */
export const triggerRefocus = async (track) => {
  if (!track?.getCapabilities) return false;

  let caps = {};
  try {
    caps = track.getCapabilities() || {};
  } catch {
    return false;
  }
  if (!caps.focusMode) return false;

  try {
    if (caps.focusMode.includes("manual") && caps.focusDistance) {
      await track.applyConstraints({
        advanced: [
          { focusMode: "manual", focusDistance: caps.focusDistance.min },
        ],
      });
    }
    if (caps.focusMode.includes("single-shot")) {
      await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] });
      return true;
    }
    if (caps.focusMode.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
      return true;
    }
    return false;
  } catch {
    return false;
  }
};
